import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { TokenStorageService } from
  '../auth/token-storage.service';

import { Authority } from
  '../constants/authorities';

export interface JwtMenuPermissions {
  [menuKey: string]: string[];
}

export interface ApplicationJwtClaims {
  sub?: string;
  iat?: number;
  exp?: number;

  tokenType?: string;
  userId?: string;
  email?: string;
  phone?: string;

  roles?: string[];
  menuPermissions?: JwtMenuPermissions;

  passwordResetRequired?: boolean;
  resetPasswordFirstLogin?: boolean;
}

export interface CurrentAccount {
  id: string | null;
  login: string;
  email: string;
  phone: string;
  roles: string[];
  menuPermissions: JwtMenuPermissions;
}

export type AccessMode = 'any' | 'all';

@Injectable({
  providedIn: 'root'
})
export class TokenDecodeService {
  private decodedToken:
    ApplicationJwtClaims | null = null;

  private decodedAccessToken:
    string | null = null;

  private readonly roleSet =
    new Set<string>();

  private readonly permissionSet =
    new Set<string>();

  private readonly claimsSubject =
    new BehaviorSubject<
      ApplicationJwtClaims | null
    >(null);

  readonly claims$: Observable<
    ApplicationJwtClaims | null
  > = this.claimsSubject.asObservable();

  constructor(
    private tokenStorage:
      TokenStorageService
  ) { }

  setToken(token: string): boolean {
    if (!token) {
      this.clear();
      return false;
    }

    try {
      const claims =
        jwtDecode<ApplicationJwtClaims>(
          token
        );

      if (
        claims.tokenType &&
        claims.tokenType !== 'ACCESS'
      ) {
        throw new Error(
          'Expected an access token'
        );
      }

      this.decodedToken = claims;
      this.decodedAccessToken = token;

      this.buildAccessIndexes(claims);
      this.claimsSubject.next(claims);

      return true;

    } catch (error) {
      console.error(
        'Unable to decode access token:',
        error
      );

      this.clear();
      return false;
    }
  }

  restore(): boolean {
    const token =
      this.tokenStorage.getAccessToken();

    if (!token) {
      this.clear();
      return false;
    }

    if (
      token === this.decodedAccessToken &&
      this.decodedToken
    ) {
      return true;
    }

    return this.setToken(token);
  }

  clear(): void {
    this.decodedToken = null;
    this.decodedAccessToken = null;

    this.roleSet.clear();
    this.permissionSet.clear();

    this.claimsSubject.next(null);
  }

  getClaims():
    ApplicationJwtClaims | null {

    return this.ensureDecoded();
  }

  getAccount(): CurrentAccount | null {
    const claims = this.ensureDecoded();

    if (!claims) {
      return null;
    }

    return {
      id: claims.userId ?? null,

      login:
        claims.sub ??
        claims.email ??
        '',

      email: claims.email ?? '',
      phone: claims.phone ?? '',

      roles: this.getRoles(),

      menuPermissions:
        claims.menuPermissions ?? {}
    };
  }

  getUserId(): string | null {
    return (
      this.ensureDecoded()?.userId ??
      null
    );
  }

  getUserName(): string | null {
    const claims = this.ensureDecoded();

    return (
      claims?.sub ??
      claims?.email ??
      null
    );
  }

  getEmail(): string | null {
    return (
      this.ensureDecoded()?.email ??
      null
    );
  }

  getPhone(): string | null {
    return (
      this.ensureDecoded()?.phone ??
      null
    );
  }

  getRoles(): string[] {
    this.ensureDecoded();

    return Array.from(
      this.roleSet
    );
  }

  getMenuPermissions():
    JwtMenuPermissions {

    return (
      this.ensureDecoded()
        ?.menuPermissions ??
      {}
    );
  }

  getPermissionKeys(): string[] {
    this.ensureDecoded();

    return Array.from(
      this.permissionSet
    );
  }

  hasAuthority(
    authority: Authority | string
  ): boolean {
    this.ensureDecoded();

    return this.roleSet.has(
      this.normalizeRole(authority)
    );
  }

  hasAnyAuthority(
    authorities:
      readonly (Authority | string)[]
  ): boolean {
    return authorities.some(authority =>
      this.hasAuthority(authority)
    );
  }

  hasAllAuthorities(
    authorities:
      readonly (Authority | string)[]
  ): boolean {
    return authorities.every(authority =>
      this.hasAuthority(authority)
    );
  }

  hasMenuPermission(
    menuKey: string,
    action: string
  ): boolean {
    return this.hasPermission(
      `${menuKey}_${action}`
    );
  }

  hasPermission(
    permissionKey: string
  ): boolean {
    this.ensureDecoded();

    return this.permissionSet.has(
      this.normalizeKey(permissionKey)
    );
  }

  hasAnyPermission(
    permissions: readonly string[]
  ): boolean {
    return permissions.some(permission =>
      this.hasPermission(permission)
    );
  }

  hasAllPermissions(
    permissions: readonly string[]
  ): boolean {
    return permissions.every(permission =>
      this.hasPermission(permission)
    );
  }

  hasPermissions(
    permissions:
      string | readonly string[],
    mode: AccessMode = 'any'
  ): boolean {
    const required =
      Array.isArray(permissions)
        ? [...permissions]
        : [permissions as string];

    if (!required.length) {
      return false;
    }

    return mode === 'all'
      ? this.hasAllPermissions(required)
      : this.hasAnyPermission(required);
  }

  getResetPasswordFirstLogin():
    boolean {

    return Boolean(
      this.ensureDecoded()
        ?.resetPasswordFirstLogin
    );
  }

  getPasswordResetRequired():
    boolean {

    return Boolean(
      this.ensureDecoded()
        ?.passwordResetRequired
    );
  }

  isAnyPasswordActionRequired():
    boolean {

    return (
      this.getResetPasswordFirstLogin() ||
      this.getPasswordResetRequired()
    );
  }

  getTokenExpiry(): number {
    const expiration =
      this.ensureDecoded()?.exp;

    return expiration
      ? expiration * 1000
      : 0;
  }

  isTokenExpired(
    offsetMilliseconds = 0
  ): boolean {
    const expiry =
      this.getTokenExpiry();

    return (
      !expiry ||
      Date.now() +
      offsetMilliseconds >=
      expiry
    );
  }

  isJwtExpired(
    token: string | null
  ): boolean {
    if (!token) {
      return true;
    }

    try {
      const claims =
        jwtDecode<ApplicationJwtClaims>(
          token
        );

      return (
        !claims.exp ||
        Date.now() >= claims.exp * 1000
      );

    } catch {
      return true;
    }
  }

  private buildAccessIndexes(
    claims: ApplicationJwtClaims
  ): void {
    this.roleSet.clear();
    this.permissionSet.clear();

    (claims.roles || [])
      .map(role =>
        this.normalizeRole(role)
      )
      .filter(Boolean)
      .forEach(role =>
        this.roleSet.add(role)
      );

    Object.entries(
      claims.menuPermissions || {}
    ).forEach(([menuKey, actions]) => {
      const menu =
        this.normalizeKey(menuKey);

      (actions || []).forEach(action => {
        const normalizedAction =
          this.normalizeKey(action);

        if (
          menu &&
          normalizedAction
        ) {
          this.permissionSet.add(
            `${menu}_${normalizedAction}`
          );
        }
      });
    });
  }

  private ensureDecoded():
    ApplicationJwtClaims | null {

    const accessToken =
      this.tokenStorage
        .getAccessToken();

    if (!accessToken) {
      if (this.decodedToken) {
        this.clear();
      }

      return null;
    }

    if (
      accessToken !==
      this.decodedAccessToken
    ) {
      this.setToken(accessToken);
    }

    return this.decodedToken;
  }

  private normalizeRole(
    value: Authority | string
  ): string {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/^ROLE_/, '');
  }

  private normalizeKey(
    value: string
  ): string {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/:/g, '_')
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
}